/* eslint-disable react/no-multi-comp */

import React, {useEffect, useRef, useState, useMemo, useCallback} from 'react'
import PropTypes from 'prop-types'
import {Grid, Typography, withStyles} from '@material-ui/core'
import Select from 'react-select'
import _ from 'lodash'

import styles from './styles'
import {
  INITIAL_OPTIONS_LIMIT,
  SEARCH_DEBOUNCE_MS,
  MAX_SELECTION_LIMIT,
  SEARCH_REQUIRED_THRESHOLD,
} from './constants'
import Option from './Option'
import NoOptionsMessage from './NoOptionsMessage'
import MultiValue from './MultiValue'
import SelectionStatus from './SelectionStatus'
import {
  formatDemographicOptions,
  getSelectedOptions,
  createSelectStyles,
  useSelectionLimits,
} from './utils'

const QualifierOptionsSelector = React.memo(props => {
  const {classes, demographic, demographicHelpers, groupTempStateDemographics} = props
  const selectRef = useRef()

  const [allOptions, setAllOptions] = useState([])
  const [displayOptions, setDisplayOptions] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  const selectedOptions = useMemo(
    () => getSelectedOptions(groupTempStateDemographics, demographic.key),
    [groupTempStateDemographics, demographic.key],
  )

  const formattedOptions = useMemo(() => formatDemographicOptions(demographic.subDemographics), [
    demographic.subDemographics,
  ])

  const {isNearLimit, isAtLimit} = useSelectionLimits(selectedOptions.length, MAX_SELECTION_LIMIT)

  const isSelectAllDisabled = useMemo(() => {
    return allOptions.length > MAX_SELECTION_LIMIT
  }, [allOptions.length])

  const requiresSearch = useMemo(() => {
    return allOptions.length > SEARCH_REQUIRED_THRESHOLD
  }, [allOptions.length])

  const debouncedSearch = useCallback(
    _.debounce(term => {
      if (!term) {
        if (requiresSearch) {
          setDisplayOptions([])
          setMenuIsOpen(false)
        } else {
          setDisplayOptions(formattedOptions.slice(0, INITIAL_OPTIONS_LIMIT))
        }
      } else {
        const filtered = formattedOptions.filter(option =>
          option.label.toLowerCase().includes(term.toLowerCase()),
        )
        setDisplayOptions(filtered.slice(0, 500))
        setMenuIsOpen(true)
      }
    }, SEARCH_DEBOUNCE_MS),
    [formattedOptions, requiresSearch],
  )

  useEffect(() => {
    setAllOptions(formattedOptions)
    if (requiresSearch) {
      setDisplayOptions([])
      setMenuIsOpen(false)
    } else {
      setDisplayOptions(formattedOptions.slice(0, INITIAL_OPTIONS_LIMIT))
      setMenuIsOpen(false)
    }
  }, [formattedOptions, requiresSearch])

  useEffect(() => {
    debouncedSearch(searchTerm)
  }, [searchTerm, debouncedSearch])

  const updateDemographic = useCallback(
    selected => {
      const foundDemographic = groupTempStateDemographics.find(({key}) => key === demographic.key)

      if (!selected || !selected.length) {
        demographicHelpers.removeDemographic(demographic.key)
      } else if (selected.length && !foundDemographic) {
        demographicHelpers.addDemographic(
          demographic.key,
          selected.map(({value}) => value),
        )
      } else {
        demographicHelpers.updateDemographic(
          demographic.key,
          selected.map(({value}) => value),
        )
      }
    },
    [groupTempStateDemographics, demographic.key, demographicHelpers],
  )

  const handleSelectChange = useCallback(
    selected => {
      if (selected && selected.length > MAX_SELECTION_LIMIT) {
        return
      }
      updateDemographic(selected)
    },
    [updateDemographic],
  )

  const handleClearSelectionsClick = useCallback(() => {
    const select = selectRef.current?.select
    if (select?.clearValue) {
      select.clearValue()
    }
  }, [])

  const handleSelectAllClick = useCallback(() => {
    if (allOptions.length > MAX_SELECTION_LIMIT) {
      return
    }
    const allOptionsFormatted = allOptions.map(option => ({
      value: option.value,
      label: option.label,
    }))
    updateDemographic(allOptionsFormatted)
  }, [allOptions, updateDemographic])

  const handleMenuOpen = useCallback(() => {
    if (!requiresSearch || searchTerm) {
      setMenuIsOpen(true)
      setTimeout(() => {
        const menuRef = selectRef.current?.select?.menuListRef

        if (menuRef) {
          menuRef.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          })
        }
      }, 100)
    }
  }, [requiresSearch, searchTerm])

  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false)
  }, [])

  const handleInputChange = useCallback(inputValue => {
    setSearchTerm(inputValue)

    if (selectRef.current?.select?.menuListRef) {
      selectRef.current.select.menuListRef.scroll({top: 0})
    }
  }, [])

  const selectStyles = useMemo(() => createSelectStyles(selectedOptions.length, isAtLimit), [
    selectedOptions.length,
    isAtLimit,
  ])

  const customComponents = useMemo(
    () => ({
      Option: props => <Option classes={classes} {...props} />,
      NoOptionsMessage: props => (
        <NoOptionsMessage
          classes={classes}
          selectedOptionsLength={selectedOptions.length}
          allOptionsLength={allOptions.length}
          {...props}
        />
      ),
      MultiValue: props => <MultiValue classes={classes} {...props} />,
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      ClearIndicator: () => null,
    }),
    [classes, selectedOptions.length, allOptions.length],
  )

  const getHelpMessage = () => {
    if (requiresSearch && searchTerm === '') {
      return `${allOptions.length} options available. Start typing to search and see results.`
    }

    if (
      searchTerm === '' &&
      displayOptions.length === INITIAL_OPTIONS_LIMIT &&
      allOptions.length > INITIAL_OPTIONS_LIMIT
    ) {
      return `Showing ${INITIAL_OPTIONS_LIMIT} of ${allOptions.length} options. Type to search for more.`
    }

    return null
  }

  return (
    <Grid>
      <Grid className={classes.header}>
        <Typography className={classes.demographicLabel}>{demographic.label}</Typography>
        {selectedOptions?.length ? (
          <Typography onClick={handleClearSelectionsClick} className={classes.clearSelections}>
            Clear All Selections
          </Typography>
        ) : (
          <Typography
            onClick={handleSelectAllClick}
            className={classes.clearSelections}
            style={{
              color: isSelectAllDisabled ? '#ccc' : undefined,
              cursor: isSelectAllDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {isSelectAllDisabled ? 'Select All (Limited)' : 'Select All'}
          </Typography>
        )}
      </Grid>
      <Select
        ref={selectRef}
        components={customComponents}
        options={displayOptions}
        value={selectedOptions}
        onChange={handleSelectChange}
        onInputChange={handleInputChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        menuIsOpen={menuIsOpen}
        openMenuOnFocus={!requiresSearch}
        openMenuOnClick={!requiresSearch}
        blurInputOnSelect={false}
        placeholder={requiresSearch ? 'Start typing to search...' : 'Type to search...'}
        closeMenuOnSelect={requiresSearch}
        isClearable={false}
        styles={selectStyles}
        isMulti
        filterOption={() => true}
        isDisabled={isAtLimit}
      />
      <SelectionStatus
        selectedOptionsLength={selectedOptions.length}
        isAtLimit={isAtLimit}
        isNearLimit={isNearLimit}
      />
      <Typography style={{fontSize: '12px', color: '#5C5E63', marginTop: '8px'}}>
        {getHelpMessage()}
      </Typography>
    </Grid>
  )
})

QualifierOptionsSelector.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  demographic: PropTypes.objectOf(PropTypes.any).isRequired,
  demographicHelpers: PropTypes.shape({
    addDemographic: PropTypes.func.isRequired,
    removeDemographic: PropTypes.func.isRequired,
    updateDemographic: PropTypes.func.isRequired,
  }).isRequired,
  groupTempStateDemographics: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default withStyles(styles)(QualifierOptionsSelector)
